import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modal, Box, Fade, Backdrop, Typography } from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";

import axiosClient from "api";
import { setCurrentUser } from "actions/authAction";
import { toastify, toastType } from "util/toastify";
import { isUserVerified } from "util/verification";

// How often (ms) to re-check verification status while the banner is showing.
const VERIFY_POLL_INTERVAL = 15000;

// Cooldown (seconds) for the "Resend Verification" button. Matches the
// backend's 2-minute resend throttle so the UI and server agree.
const RESEND_COOLDOWN = 120;

const modalStyle = {
  display: "flex",
  position: "absolute",
  top: "50%",
  left: "50%",
  textAlign: "center",
  flexDirection: "column",
  alignItems: "center",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: "background.paper",
  padding: "32px 28px",
  borderRadius: "20px",
};

const VerificationContext = createContext({
  verified: true,
  gated: false,
  requireVerified: (action) => {
    if (typeof action === "function") action();
    return true;
  },
  openVerifyModal: () => {},
  resend: () => {},
  cooldown: 0,
  sending: false,
});

export const useVerification = () => useContext(VerificationContext);

export const VerificationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const verified = isUserVerified(auth?.user);
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const userId = auth?.user?.userId;

  // Gating is only relevant for a logged-in user who hasn't verified yet.
  const gated = isAuthenticated && !verified;

  const [open, setOpen] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  // While the user is gated (logged in but unverified), keep the user record in
  // sync with the backend so the banner disappears the moment they verify —
  // without needing a manual page refresh. The verification often happens in a
  // different tab or device, so we poll on an interval and also re-check
  // whenever this tab regains focus.
  useEffect(() => {
    if (!gated || !userId) return undefined;

    let cancelled = false;
    const refreshUser = async () => {
      try {
        const res = await axiosClient.get(`/api/users/${userId}`);
        const user = res?.data?.data?.user;
        if (!cancelled && user) dispatch(setCurrentUser(user));
      } catch (err) {
        // Ignore transient failures — we'll retry on the next tick/focus.
      }
    };

    const interval = setInterval(refreshUser, VERIFY_POLL_INTERVAL);
    window.addEventListener("focus", refreshUser);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", refreshUser);
    };
  }, [gated, userId, dispatch]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const openVerifyModal = useCallback(() => setOpen(true), []);
  const closeVerifyModal = useCallback(() => setOpen(false), []);

  // Run `action` only when verified; otherwise prompt the user to verify.
  // Returns true when the action was allowed to run.
  const requireVerified = useCallback(
    (action) => {
      if (gated) {
        openVerifyModal();
        return false;
      }
      if (typeof action === "function") action();
      return true;
    },
    [gated, openVerifyModal]
  );

  const resend = useCallback(async () => {
    if (cooldown > 0 || sending) return;

    const user = auth?.user || {};
    if (!user.email) {
      toastify(
        "We couldn't find your email. Please log in again and retry.",
        toastType.error
      );
      return;
    }

    setSending(true);
    try {
      await axiosClient.post("/auth/resend-email", {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
      toastify(
        "Verification email sent. Please check your inbox.",
        toastType.success
      );
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      console.error(err);
      // If the server throttled the request (429), start the cooldown too so
      // the button locks, and surface the server's "please wait" message.
      if (err?.response?.status === 429) {
        setCooldown(RESEND_COOLDOWN);
      }
      const serverMessage = err?.response?.data?.message;
      toastify(
        serverMessage ||
          "Could not send the verification email. Please try again.",
        toastType.error
      );
    } finally {
      setSending(false);
    }
  }, [auth, cooldown, sending]);

  const resendLabel = sending
    ? "Sending..."
    : cooldown > 0
    ? `Resend in ${cooldown}s`
    : "Resend Verification Email";

  return (
    <VerificationContext.Provider
      value={{
        verified,
        gated,
        requireVerified,
        openVerifyModal,
        resend,
        cooldown,
        sending,
      }}
    >
      {children}

      <Modal
        open={open}
        onClose={closeVerifyModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <Box sx={modalStyle} className="max-md:max-w-[90vw]">
            <button
              className="absolute -top-4 -right-4 outline-none w-8 h-8 rounded-full p-0 bg-white shadow"
              onClick={closeVerifyModal}
            >
              <CloseIcon />
            </button>

            <Typography variant="h6" component="h2" className="!mb-3">
              Verify your email first
            </Typography>

            <p className="text-[15px] text-gray-600 mb-6">
              To request services or message members, please verify your email
              address. We've sent a verification link to your inbox — click it
              to unlock all features.
            </p>

            <button
              type="button"
              onClick={resend}
              disabled={cooldown > 0 || sending}
              className={
                "py-3 px-7 border-none outline-none rounded-full focus:outline-none transition-colors duration-300 " +
                (cooldown > 0 || sending
                  ? "bg-[#E9E9E9] text-[#999] cursor-not-allowed"
                  : "bg-main text-white cursor-pointer hover:bg-[#35f016]")
              }
            >
              {resendLabel}
            </button>
          </Box>
        </Fade>
      </Modal>
    </VerificationContext.Provider>
  );
};
